import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Mail, Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/admin";
    const [form, setForm] = useState({ email: "", password: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const user = await login(form.email, form.password);
            toast.success("Welcome back!");
            let target = next;
            if (user?.role === "admin" || user?.role === "super_admin") target = "/admin";
            else if (["sales", "support", "designer", "production", "painting", "packing", "finance", "operations"].includes(user?.role)) target = "/team";
            else target = "/";
            navigate(target);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-6 py-10" data-testid="login-page">
            <div className="w-full max-w-md rounded-4xl glass-strong p-8 relative overflow-hidden">
                <div className="ambient-orb bg-brand-peach -top-16 -left-16 w-56 h-56" />
                <div className="ambient-orb bg-brand-lavender -bottom-16 -right-16 w-56 h-56" />
                <div className="relative z-10">
                    <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-primary to-brand-gold flex items-center justify-center mb-4">
                        <LogIn className="w-6 h-6 text-white" />
                    </div>
                    <h1 className="font-display text-3xl font-bold text-center tracking-tight">Team login</h1>
                    <p className="text-sm text-muted-foreground text-center mt-1">Admin & team access only. Customers can checkout as a guest.</p>

                    <form onSubmit={submit} className="space-y-3 mt-6">
                        <div className="relative">
                            <Mail className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" className="rounded-xl h-12 bg-white/60 pl-11" />
                        </div>
                        <div className="relative">
                            <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" className="rounded-xl h-12 bg-white/60 pl-11" />
                        </div>
                        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full h-12 bg-primary hover:bg-primary/90">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
                        </Button>
                    </form>
                    <div className="mt-5 flex items-center justify-center text-sm">
                        <Link to="/forgot-password" className="text-primary hover:underline">Forgot password?</Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
